function isHistoricalRole(role) {
  return ["COMPARATOR_EOB", "COMPARATOR_AWARD_AGREEMENT"].includes(role);
}

export function getPricingEvidenceLabel(document, page) {
  const role = document.pageAssignments?.find((assignment) => assignment.pages?.includes(page))?.role || document.role;
  if (role === "TARGET_CLAIM_REMITTANCE") return "case QPA EOB";
  if (isHistoricalRole(role)) return "Exhibit B";
  return "";
}

export function getHistoricalPaymentRows(pricing, manifest) {
  return (pricing?.comparatorCandidates || []).filter((item) => item.sourceRefs?.some((ref) =>
    manifest.some((document) => document.id === ref.documentId &&
      Number(document.version) === Number(ref.documentVersion) &&
      getPricingEvidenceLabel(document, ref.page) === "Exhibit B")
  )).map((item) => ({
    code: item.cptHcpcs || "—",
    modifiers: item.modifiers || "—",
    date: item.serviceDate || "—",
    amount: [item.currency, item.amount].filter(Boolean).join(" ") || "—",
    basis: item.paymentBasis || "—",
    sourceRefs: item.sourceRefs,
  }));
}

export function getAnalysisReviewWarnings(clinical, pricing) {
  return [...new Set([
    ...(clinical?.missingInformation || []), ...(clinical?.riskFlags || []),
    ...(clinical?.findings || []).filter((item) => item.evidenceType !== "DOCUMENTED")
      .map((item) => `${item.evidenceType}: ${item.finding}`),
    ...(pricing?.missingInformation || []),
    ...(pricing?.serviceLineAssessments || []).flatMap((item) => item.missingEvidence || []),
    ...(pricing?.comparatorCandidates || []).flatMap((item) => item.limitations || []),
    ...(pricing?.paymentFacts || []).flatMap((item) => item.limitations || []),
    ...(pricing?.codeDiscrepancies || []).map((item) => item.details),
  ].filter(Boolean))];
}

export function buildCombinedDocument(clinical, pricing, manifest) {
  if (!clinical?.summary || !Array.isArray(clinical.findings) ||
      !pricing?.pricingSupportNarrative?.sections?.length) {
    throw new Error("Both medical analysis and a pricing-support narrative are required. Rerun older analyses that do not include the narrative.");
  }
  const citation = (refs) => (refs || []).map((ref) => {
    const document = manifest.find((item) => item.id === ref.documentId && Number(item.version) === ref.documentVersion);
    if (!document || (ref.page !== null && (!Number.isInteger(ref.page) || ref.page < 1))) {
      throw new Error("An analysis citation does not match the current case sources.");
    }
    const label = getPricingEvidenceLabel(document, ref.page);
    return `${label ? `${label}: ` : ""}${document.name}, version ${ref.documentVersion}, ${ref.page === null ? "physical page unavailable" : `p. ${ref.page}`}`;
  }).join("; ");
  const evidence = clinical.findings.map((finding) =>
    `- ${finding.finding}\n  ${finding.relevance}${finding.sourceRefs?.length ? `\n  Sources: ${citation(finding.sourceRefs)}` : ""}`
  );
  const sections = pricing.pricingSupportNarrative.sections.map((section) =>
    `${section.heading}\n\n${section.text}${section.sourceRefs?.length ? `\nSources: ${citation(section.sourceRefs)}` : ""}`
  );
  const cell = (value) => String(value).replace(/\|/g, "\\|").replace(/[\r\n]+/g, " ");
  const historicalPayments = getHistoricalPaymentRows(pricing, manifest);
  const table = historicalPayments.length ? [
    "Historical Payments — Exhibit B",
    [
      "| CPT/HCPCS | Modifiers | Service date | Amount | Payment basis | Evidence |",
      "| --- | --- | --- | --- | --- | --- |",
      ...historicalPayments.map((row) =>
        `| ${[row.code, row.modifiers, row.date, row.amount, row.basis,
          citation(row.sourceRefs)].map(cell).join(" | ")} |`),
    ].join("\n"),
  ] : [];
  const exhibitDocuments = new Set(historicalPayments.flatMap((row) =>
    row.sourceRefs.map((ref) => `${ref.documentId}:${ref.documentVersion}`)));
  return [
    "Combined Medical Necessity and Pricing Support",
    "Medical Necessity Evidence Review",
    clinical.summary, ...evidence,
    pricing.pricingSupportNarrative.title, ...sections,
    ...table,
    "Exhibits and Source Documents",
    ...manifest.map((item) => {
      const labels = [...new Set([item.role, ...(item.pageAssignments || []).map((assignment) => assignment.role)]
        .map((role) => role === "TARGET_CLAIM_REMITTANCE" ? "case QPA EOB"
          : isHistoricalRole(role) ? "Exhibit B" : "").filter(Boolean))];
      if (!labels.includes("Exhibit B") && exhibitDocuments.has(`${item.id}:${item.version}`)) labels.push("Exhibit B");
      return `${labels.length ? `${labels.join(" / ")}: ` : ""}${item.name} - version ${item.version}`;
    }),
    "Human verification is required. This document does not determine IDR eligibility and has not been filed or sent.",
  ].join("\n\n");
}
